import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import { windowsTableStyles } from '../../styles/windowsTableStyles';
import { actionButtonStyles } from '../../styles/actionButton';
import BreadcrumbNav from '../BreadcrumbNav';

const tableStyles = windowsTableStyles();

const WorkflowTab = () => {
  const { t } = useTranslation(['pages', 'common', 'buttons', 'workflows', 'messages']);
  const { user } = useSelector((state) => state.userLoaded);
  const navigate = useNavigate();
  const isManager = user?.role === 'Manager';

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/workflows');
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setSnackbarMessage(t('messages:error.fetching_workflows') || 'Error fetching workflows');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/workflows/create');
  };

  const handleEdit = (workflow) => {
    navigate(`/workflows/edit/${workflow._id}`);
  };

  const handleDelete = (workflow) => {
    setWorkflowToDelete(workflow);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!workflowToDelete) return;

    try {
      setLoading(true);
      await axios.delete(`/api/workflows/${workflowToDelete._id}`);
      setSnackbarMessage(t('workflows:deleted_successfully') || 'Workflow deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchWorkflows();
    } catch (error) {
      console.error('Error deleting workflow:', error);
      setSnackbarMessage(error.response?.data?.message || t('messages:error.deleting_workflow') || 'Error deleting workflow');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const handleToggleEnabled = async (workflow) => {
    try {
      await axios.put(`/api/workflows/${workflow._id}`, {
        ...workflow,
        enabled: !workflow.enabled
      });
      setSnackbarMessage(
        workflow.enabled 
          ? (t('workflows:disabled_successfully') || 'Workflow disabled')
          : (t('workflows:enabled_successfully') || 'Workflow enabled')
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchWorkflows();
    } catch (error) {
      console.error('Error toggling workflow:', error);
      setSnackbarMessage(error.response?.data?.message || t('messages:error.updating_workflow') || 'Error updating workflow');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleViewExecutions = (workflow) => {
    navigate(`/workflows/${workflow._id}/executions`);
  };


  const getTriggerDisplay = (workflow) => {
    const type = workflow.trigger?.type;
    const isDoc = type === 'template_used';
    const isEmail = type === 'email_sent';
    const prefix = isDoc
      ? (t('workflows:document_from_template_created') || 'Document from template created')
      : isEmail
        ? (t('workflows:email_template_sent') || 'Email template sent')
        : null;
    if (!prefix) return type || 'Unknown';
    const ids = isDoc
      ? (workflow.trigger?.filters?.templateIds || [])
      : (workflow.trigger?.filters?.emailTemplateIds || []);
    if (ids.length === 0) return `${prefix}: All Templates`;
    if (ids.length === 1) {
      const tpl = ids[0];
      const name = typeof tpl === 'object' && tpl?.name ? tpl.name : 'Unknown Template';
      return `${prefix}: ${name}`;
    }
    return `${prefix}: ${ids.length} Templates`;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, mt: 2 }}>
        <BreadcrumbNav title={t('workflows:title') || 'Workflow Management'} />
        {isManager && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={actionButtonStyles()}
          >
            {t('workflows:create') || 'Create Workflow'}
          </Button>
        )}
      </Box>

      <Box>
        {loading && workflows.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>{t('common:loading') || 'Loading...'}</Typography>
          </Box>
        ) : workflows.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">
              {t('workflows:no_workflows') || 'No workflows found. Create your first workflow to get started.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{border: '1px solid #d1d1d1', maxHeight: 500, overflowY: 'auto', width: '100%' }}>
            <Table size="small" sx={tableStyles.container}>
              <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 1000 }}>
                <TableRow sx={{ backgroundColor: '#ffffff' }}>
                  <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:name') || 'Name'}</TableCell>
                  <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:description') || 'Description'}</TableCell>
                  <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:trigger') || 'Trigger'}</TableCell>
                  <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:status_label') || 'Status'}</TableCell>
                  <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('common:actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflows.map((workflow) => (
                  <TableRow
                    key={workflow._id}
                    sx={{ ...tableStyles.row, borderBottom: '1px solid rgb(188, 187, 187)' }}
                  >
                    <TableCell sx={{ ...tableStyles.cell, padding: '4px 16px', fontSize: '12px', verticalAlign: 'middle' }}>{workflow.name}</TableCell>
                    <TableCell sx={{ ...tableStyles.cell, padding: '4px 16px', fontSize: '12px', verticalAlign: 'middle', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {workflow.description || '-'}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell, padding: '4px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                      {getTriggerDisplay(workflow)}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell, padding: '4px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                      <Chip
                        label={workflow.enabled ? (t('workflows:enabled') || 'Enabled') : (t('workflows:disabled') || 'Disabled')}
                        color={workflow.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell, padding: '4px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title={workflow.enabled ? (t('workflows:disable') || 'Disable') : (t('workflows:enable') || 'Enable')}>
                          <Switch
                            checked={workflow.enabled}
                            onChange={() => handleToggleEnabled(workflow)}
                            size="small"
                          />
                        </Tooltip>
                        <Tooltip title={t('workflows:view_executions') || 'View Executions'}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewExecutions(workflow)}
                            sx={{ color: '#2B80B6' }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isManager && (
                          <>
                            <Tooltip title={t('buttons:edit') || 'Edit'}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(workflow)}
                                sx={{ color: '#2B80B6' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('buttons:delete') || 'Delete'}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(workflow)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setWorkflowToDelete(null);
        }}
      >
        <DialogTitle>{t('workflows:delete_workflow') || 'Delete Workflow'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('workflows:delete_confirmation', { name: workflowToDelete?.name }) || 
              `Are you sure you want to delete "${workflowToDelete?.name}"? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setWorkflowToDelete(null);
          }}>
            {t('buttons:cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('common:delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowTab;
